import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    BU_QUALIFICATION_URL_PREFIX,
    COMPLIANCE_REQUIREMENT_URL_PREFIX,
    complianceRequirementCollectionUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ComplianceRequirement,
    ComplianceRequirementWrite,
} from "~/pages/supplier-360/types/Supplier360Types";

/**
 * `POST supplier_360/compliance_requirement/` — docs §2.8, the "Add compliance record" dialog.
 * `state` is never sent, so the server defaults the new record to `pending`.
 *
 * Invalidates every requirement page, since the new row can land on any of them, and the BU
 * qualifications, whose `status` the server recomputes (§2.7).
 */
export function useCreateComplianceRequirementMutation() {
    const queryClient = useQueryClient();

    return useMutation<ComplianceRequirement, DefaultError, ComplianceRequirementWrite>({
        mutationFn: async (payload) => {
            const {data} = await queryHttp.post<ComplianceRequirement>(
                complianceRequirementCollectionUrl(),
                payload,
            );
            return data;
        },
        onSuccess: (created) => {
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string'
                        && (key.startsWith(COMPLIANCE_REQUIREMENT_URL_PREFIX) || key.startsWith(BU_QUALIFICATION_URL_PREFIX));
                },
            });
            notificationStore.show(`${created.name} added.`, {
                severity: 'success',
                id: `compliance-requirement-add-success-${created.entity}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entity}) => {
            notificationStore.show('Could not add the compliance record. Please try again.', {
                severity: 'error',
                id: `compliance-requirement-add-error-${entity}`,
                autoHideDuration: 4000,
            });
        },
    });
}
